import { createFileRoute, Link } from "@tanstack/react-router";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Badge } from "@patient-watch/ui/badge";
import { Button } from "@patient-watch/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { Loader2, MoreVertical, Pencil, Plus, Trash } from "lucide-react";
import { useConsentBodies } from "@/features/consent/api/selectConsentBodies";
import { useDeleteConsentBody } from "@/features/consent/api/deleteConsentBody";

const ConsentsBuilder = () => {
    const { data: consentBodies, isLoading } = useConsentBodies();
    const { mutate: deleteConsentBody } = useDeleteConsentBody();

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Consents", route: { to: "/builder/consents" } },
    ]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="font-semibold text-3xl tracking-tight">Consents</h1>
                    <p className="text-muted-foreground">
                        Author reusable consent content and attach it to templates. Selected consents are shown to
                        patients on their diary when a quickstart runs.
                    </p>
                </div>
                <Button size="sm" asChild>
                    <Link to="/builder/consents/$consentBodyId" params={{ consentBodyId: "new" }}>
                        <Plus className="mr-2 w-4 h-4" />
                        New Consent
                    </Link>
                </Button>
            </div>

            <ui.Card>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Version</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Grants</TableHead>
                            <TableHead className="w-10" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {consentBodies?.length ? (
                            consentBodies.map((body) => (
                                <TableRow key={body.consent_body_id}>
                                    <TableCell className="font-medium">{body.consent_name}</TableCell>
                                    <TableCell className="text-sm">{body.consent_version}</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={body.is_published ? "default" : "secondary"}
                                            className="text-xs"
                                        >
                                            {body.is_published ? "Published" : "Draft"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-wrap gap-1">
                                            {body.grants_care_team_sharing ? (
                                                <Badge variant="outline" className="text-2xs">
                                                    Care team
                                                </Badge>
                                            ) : null}
                                            {body.grants_analytics_sharing ? (
                                                <Badge variant="outline" className="text-2xs">
                                                    Analytics
                                                </Badge>
                                            ) : null}
                                            {!body.grants_care_team_sharing && !body.grants_analytics_sharing ? (
                                                <span className="text-muted-foreground text-2xs">None</span>
                                            ) : null}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <ui.Popover>
                                            <ui.PopoverTrigger asChild>
                                                <Button variant="ghost" size="icon">
                                                    <MoreVertical size={20} />
                                                </Button>
                                            </ui.PopoverTrigger>
                                            <ui.PopoverContent align="end" className="p-1 w-40">
                                                <div className="flex flex-col">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="justify-start gap-2 px-2 py-1.5 w-full h-auto"
                                                        asChild
                                                    >
                                                        <Link
                                                            to="/builder/consents/$consentBodyId"
                                                            params={{ consentBodyId: body.consent_body_id }}
                                                        >
                                                            <Pencil size={16} />
                                                            Edit
                                                        </Link>
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="justify-start gap-2 px-2 py-1.5 w-full h-auto text-red-600 hover:text-red-600"
                                                        onClick={() => deleteConsentBody(body.consent_body_id)}
                                                    >
                                                        <Trash size={16} />
                                                        Delete
                                                    </Button>
                                                </div>
                                            </ui.PopoverContent>
                                        </ui.Popover>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} className="py-12 text-muted-foreground text-center">
                                    <p className="mb-2">No consents yet.</p>
                                    <Button variant="link" size="sm" asChild>
                                        <Link to="/builder/consents/$consentBodyId" params={{ consentBodyId: "new" }}>
                                            Create your first consent
                                        </Link>
                                    </Button>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/consents/")({
    component: ConsentsBuilder,
});
